/**
 * IframeExtension (T021 - US2)
 * Custom TipTap Node for embedded iframes (YouTube, Vimeo, Spotify, etc.)
 */

import { Node, mergeAttributes } from '@tiptap/core';

/**
 * Safe sandbox policy, force-applied on render regardless of the pasted value.
 * Mirrors the force-overwrite in the backend and frontend HTML sanitizers so the
 * editor never renders a more permissive sandbox than what is ultimately stored.
 */
const SAFE_SANDBOX =
  'allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox';

const TRUSTED_IFRAME_DOMAINS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'open.spotify.com',
  'podcasters.spotify.com',
  'play.libsyn.com',
  'w.soundcloud.com',
  'embed.music.apple.com',
  'embed.podcasts.apple.com',
];

function isTrustedDomain(src: string): boolean {
  try {
    const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
    const url = new URL(normalizedSrc);
    return TRUSTED_IFRAME_DOMAINS.includes(url.hostname);
  } catch {
    return false;
  }
}

export const IframeExtension = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => {
          const src = element.getAttribute('src');
          if (src && isTrustedDomain(src)) return src;
          return null;
        },
      },
      width: {
        default: '100%',
        parseHTML: (element) => element.getAttribute('width') || '100%',
      },
      height: {
        default: '315',
        parseHTML: (element) => element.getAttribute('height') || '315',
      },
      frameborder: {
        default: '0',
        parseHTML: (element) => element.getAttribute('frameborder') || '0',
      },
      allowfullscreen: {
        default: true,
        parseHTML: (element) => element.hasAttribute('allowfullscreen'),
      },
      allow: {
        default: null,
        parseHTML: (element) => element.getAttribute('allow'),
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'iframe' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'iframe',
      mergeAttributes(HTMLAttributes, {
        allowfullscreen: HTMLAttributes.allowfullscreen ? '' : undefined,
        // Force the safe sandbox policy; never trust a pasted sandbox value.
        sandbox: SAFE_SANDBOX,
      }),
    ];
  },
});

export { TRUSTED_IFRAME_DOMAINS, isTrustedDomain };
